import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ReferenceDim } from '../types';
import { defaultReferenceDim as localDefaultReferenceDim } from '../config/defaultReferenceDim';
import { captureException } from '../utils/captureException';

interface UseDefaultReferenceDimResult {
  defaultReferenceDim: ReferenceDim | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Global cache to avoid refetching on every component mount
let cachedReferenceDim: ReferenceDim | null = null;
let fetchPromise: Promise<ReferenceDim> | null = null;
let isFetching = false;

export const useDefaultReferenceDim = (): UseDefaultReferenceDimResult => {
  const { user } = useAuth();
  // Initialize with cached data or local fallback
  const [defaultReferenceDim, setDefaultReferenceDim] = useState<ReferenceDim | null>(
    cachedReferenceDim || localDefaultReferenceDim
  );
  const [loading, setLoading] = useState<boolean>(!cachedReferenceDim && !!user);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaultReferenceDim = useCallback(async () => {
    // If we have cached data, use it immediately
    if (cachedReferenceDim) {
      setDefaultReferenceDim(cachedReferenceDim);
      setLoading(false);
      return;
    }

    // If user is not authenticated, use local fallback
    if (!user) {
      setDefaultReferenceDim(localDefaultReferenceDim);
      setLoading(false);
      return;
    }

    // If already fetching, wait for the existing promise
    if (isFetching && fetchPromise) {
      try {
        const data = await fetchPromise;
        setDefaultReferenceDim(data);
        setLoading(false);
        return;
      } catch (err) {
        captureException(err, { tags: { feature: 'useDefaultReferenceDim' }, extra: { action: 'fetchDefaultReferenceDim' } });
        // If fetch fails, fallback to local
        setDefaultReferenceDim(localDefaultReferenceDim);
        setError(err instanceof Error ? err.message : 'Failed to fetch homologacion');
        setLoading(false);
        return;
      }
    }

    // Start new fetch
    isFetching = true;
    setLoading(true);
    setError(null);

    fetchPromise = (async (): Promise<ReferenceDim> => {
      try {
        const idToken = await user.getIdToken();
        if (!idToken) {
          throw new Error('No idToken found');
        }

        const url = `${process.env.REACT_APP_API_URL || ''}/api/reference_dimensions/default`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Cache the result only from backend response (never update after initial fetch)
        if (!cachedReferenceDim) {
          cachedReferenceDim = data;
        }
        setDefaultReferenceDim(data);
        setLoading(false);
        isFetching = false;
        fetchPromise = null;
        
        return data;
      } catch (err) {
        isFetching = false;
        fetchPromise = null;
        
        // Fallback to local config on error (do not cache local fallback)
        console.warn('Failed to fetch default homologacion from backend, using local fallback:', err);
        setDefaultReferenceDim(localDefaultReferenceDim);
        setError(err instanceof Error ? err.message : 'Failed to fetch homologacion');
        setLoading(false);
        
        return localDefaultReferenceDim;
      }
    })();

    try {
      await fetchPromise;
    } catch (err) {
      captureException(err, { tags: { feature: 'useDefaultReferenceDim' }, extra: { action: 'fetchDefaultReferenceDim' } });
      // Error already handled in the promise
    }
  }, [user]);

  useEffect(() => {
    fetchDefaultReferenceDim();
  }, [fetchDefaultReferenceDim]);

  const refetch = useCallback(() => {
    // Clear cache and fetch again
    cachedReferenceDim = null;
    fetchPromise = null;
    isFetching = false;
    fetchDefaultReferenceDim();
  }, [fetchDefaultReferenceDim]);

  return {
    defaultReferenceDim,
    loading,
    error,
    refetch,
  };
};

